import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, ExternalLink, Heart, CheckCircle2, RefreshCw, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VariantCard({ variant, isSelected, isLikedAlready, onLike, isSubmitting, embedScale }) {
  const [hover, setHover] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");

  const handleLikeClick = (e) => {
    e.preventDefault();
    if (isLikedAlready) return;
    if (!showComment) {
      setShowComment(true);
      return;
    }
    onLike(variant.project_id, comment);
  };

  return (
    <div
      className={`relative group rounded-2xl overflow-hidden border-2 transition-all duration-300 ${isSelected ? "border-emerald-500 shadow-2xl shadow-emerald-500/20" : "border-zinc-800 hover:border-zinc-600"} bg-zinc-950`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`compare-variant-${variant.template_id}`}
    >
      {/* Iframe-Vorschau */}
      <div className="relative aspect-[16/10] bg-black overflow-hidden">
        {variant.pending ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-3">
            <RefreshCw size={28} className="animate-spin" />
            <span className="text-sm font-medium">Wird generiert…</span>
          </div>
        ) : (
          <>
            <iframe
              src={variant.preview_url}
              title={variant.label}
              className="absolute origin-top-left pointer-events-none"
              style={{
                width: `${100 / embedScale}%`,
                height: `${100 / embedScale}%`,
                transform: `scale(${embedScale})`,
                border: 0,
              }}
              sandbox="allow-scripts allow-same-origin"
              loading="lazy"
            />
            <button
              onClick={() => window.open(variant.preview_url, "_blank")}
              className={`absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent transition-opacity flex items-end justify-end p-4 ${hover ? "opacity-100" : "opacity-0"}`}
            >
              <span className="bg-white text-black px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1.5">
                Vollbild ansehen <ExternalLink size={14} />
              </span>
            </button>
          </>
        )}
      </div>

      {/* Card Content */}
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-[10px] font-bold tracking-[.15em] text-zinc-500 uppercase">{variant.template_id.replace(/-/g, " ")}</span>
          {variant.subpages_count > 0 && (
            <span className="text-[10px] font-bold tracking-wider text-violet-300 bg-violet-950/40 border border-violet-900/60 px-2 py-0.5 rounded-full uppercase">
              +{variant.subpages_count} Unterseiten
            </span>
          )}
          {variant.subpages_pending && (
            <span className="text-[10px] font-bold tracking-wider text-amber-400 bg-amber-950/40 border border-amber-900/60 px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
              <RefreshCw size={9} className="animate-spin" /> +Unterseiten
            </span>
          )}
          {isSelected && (
            <span className="text-[10px] font-bold tracking-wider text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
              <CheckCircle2 size={11} /> Ausgewählt
            </span>
          )}
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">{variant.label}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed mb-4">{variant.mood}</p>

        {showComment && !isLikedAlready && !variant.pending && (
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional: Was gefällt Ihnen besonders? Was sollten wir anpassen?"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-200 mb-3 resize-none focus:border-emerald-500 focus:outline-none"
            rows={3}
            data-testid={`comment-${variant.template_id}`}
          />
        )}

        <button
          onClick={handleLikeClick}
          disabled={variant.pending || isSubmitting || isLikedAlready}
          className={`w-full py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-all ${
            isLikedAlready
              ? "bg-emerald-500 text-black cursor-default"
              : isSelected
              ? "bg-emerald-500 hover:bg-emerald-400 text-black"
              : "bg-white hover:bg-zinc-200 text-black"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid={`like-${variant.template_id}`}
        >
          {isLikedAlready ? (
            <><CheckCircle2 size={16} /> Sie haben gewählt</>
          ) : showComment ? (
            <><Heart size={16} /> Wahl bestätigen</>
          ) : (
            <><Heart size={16} /> Diese gefällt mir</>
          )}
        </button>
      </div>
    </div>
  );
}

export default function ComparePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [embedScale, setEmbedScale] = useState(0.4);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/public/compare/${token}`);
      setData(r.data);
    } catch (err) {
      toast.error("Vergleichs-Link nicht gefunden oder abgelaufen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Polling: jede 8s aktualisieren falls noch Varianten pending sind
    const id = setInterval(() => {
      if (data?.variants?.some(v => v.pending)) fetchData();
    }, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, data?.variants?.length]);

  // Responsive iframe-scale
  useEffect(() => {
    const fn = () => {
      const w = window.innerWidth;
      if (w < 640) setEmbedScale(0.32);
      else if (w < 1024) setEmbedScale(0.38);
      else setEmbedScale(0.42);
    };
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const handleLike = async (projectId, comment) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/compare/${token}/like`, { project_id: projectId, comment });
      toast.success("Vielen Dank! Ihre Auswahl wurde übermittelt.");
      await fetchData();
    } catch (err) {
      toast.error("Fehler beim Übermitteln");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <RefreshCw size={28} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Link nicht gefunden</h1>
          <p className="text-zinc-400">Dieser Vergleichs-Link ist abgelaufen oder ungültig. Bitte kontaktieren Sie Ihren Berater.</p>
        </div>
      </div>
    );
  }

  const liked = (data.feedback || []).map(f => f.project_id);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 px-6 sm:px-12 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
              <Sparkles size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[.2em] text-zinc-500 font-bold">signalSolution</div>
              <div className="text-sm font-bold">Konzept-Präsentation</div>
            </div>
          </div>
          {data.salesperson && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Ihr Berater</div>
              <div className="text-sm font-bold">{data.salesperson.name}</div>
              {data.salesperson.email && (
                <a href={`mailto:${data.salesperson.email}`} className="text-xs text-zinc-400 hover:text-white flex items-center justify-end gap-1 mt-1">
                  <Mail size={11} /> {data.salesperson.email}
                </a>
              )}
              {data.salesperson.phone && (
                <a href={`tel:${data.salesperson.phone}`} className="text-xs text-zinc-400 hover:text-white flex items-center justify-end gap-1">
                  <Phone size={11} /> {data.salesperson.phone}
                </a>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 sm:px-12 py-16 sm:py-24 max-w-7xl mx-auto">
        <div className="text-[11px] uppercase tracking-[.3em] text-violet-400 font-bold mb-4">Für {data.lead.company_name}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
          Wir haben <em className="not-italic bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">{data.variants.length} Konzepte</em><br />
          für Sie entwickelt.
        </h1>
        <p className="text-lg text-zinc-400 max-w-2xl leading-relaxed">
          Jede Variante zeigt eine andere Persönlichkeit Ihrer zukünftigen Website — gleiche Inhalte, unterschiedliche Stimmungen. Wählen Sie Ihren Favoriten.
        </p>
        {liked.length > 0 && (
          <div className="mt-6 inline-flex items-center gap-2 bg-emerald-950/40 border border-emerald-800 px-4 py-2 rounded-full text-sm text-emerald-400">
            <CheckCircle2 size={14} /> Sie haben bereits eine Auswahl getroffen — Sie können noch eine andere wählen oder kommentieren.
          </div>
        )}
      </section>

      {/* Variants Grid */}
      <section className="px-6 sm:px-12 pb-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {data.variants.map((v) => (
            <VariantCard
              key={v.project_id}
              variant={v}
              isSelected={data.selected_project_id === v.project_id}
              isLikedAlready={liked.includes(v.project_id)}
              onLike={handleLike}
              isSubmitting={submitting}
              embedScale={embedScale}
            />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8 px-6 text-center text-xs text-zinc-600">
        Diese Vorschau wurde mit signalSolution erstellt. Fragen? Antworten Sie einfach auf die E-Mail Ihres Beraters.
      </footer>
    </div>
  );
}
